import { Injectable } from '@angular/core';
import { VexConfigService } from '@vex/config/vex-config.service';
import { VexConfig, VexConfigName } from '@vex/config/vex-config.interface';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { AdminService } from './admin.service';

@Injectable({
  providedIn: 'root'
})
export class UserThemePreferenceService {
  private initialized = false;
  private hydrating = false;
  private lastSavedJson = '';
  private readonly platform = 'WEB';

  constructor(
    private readonly configService: VexConfigService,
    private readonly admin: AdminService
  ) {}

  init(): void {
    if (this.initialized) {
      return;
    }

    const userId = this.getUserId();
    if (!userId) {
      return;
    }

    this.initialized = true;
    this.watchConfigChanges(userId);
    this.hydrateFromCacheAndDb(userId);
  }

  restoreDefault(): void {
    const userId = this.getUserId();

    if (userId) {
      localStorage.removeItem(this.localKey(userId));
      sessionStorage.removeItem(this.sessionLoadedKey(userId));
    }

    this.configService.setConfig(VexConfigName.poseidon);
  }

  private hydrateFromCacheAndDb(userId: number): void {
    this.hydrating = true;

    const cached = this.readLocalConfig(userId);
    if (cached) {
      this.applyConfig(cached);
      this.lastSavedJson = this.toJson(cached);
    }

    if (sessionStorage.getItem(this.sessionLoadedKey(userId)) === 'true') {
      this.hydrating = false;
      return;
    }

    this.admin.getUserThemePreference(userId, this.platform).subscribe({
      next: (res: any) => {
        const serverConfig = this.extractConfig(res);

        if (serverConfig) {
          this.applyConfig(serverConfig);
          this.saveLocalConfig(userId, serverConfig);
          this.lastSavedJson = this.toJson(serverConfig);
        }

        sessionStorage.setItem(this.sessionLoadedKey(userId), 'true');
        this.hydrating = false;
      },
      error: () => {
        this.hydrating = false;
      }
    });
  }

  private watchConfigChanges(userId: number): void {
    this.configService.config$.pipe(
      debounceTime(700),
      map(config => this.cloneConfig(config)),
      distinctUntilChanged((a, b) => this.toJson(a) === this.toJson(b))
    ).subscribe(config => {
      if (this.hydrating) {
        return;
      }

      const configJson = this.toJson(config);
      if (configJson === this.lastSavedJson) {
        return;
      }

      this.lastSavedJson = configJson;
      this.saveLocalConfig(userId, config);

      this.admin.postUserThemePreference({
        userid: userId,
        platform: this.platform,
        configname: config.id,
        themeclassname: config.style.themeClassName,
        colorscheme: config.style.colorScheme,
        configjson: config,
        loginuserid: userId
      }).subscribe({
        error: () => {
          // Local cache is still the source for the next app load if the save fails.
        }
      });
    });
  }

  private applyConfig(config: VexConfig): void {
    if (config?.id) {
      this.configService.setConfig(config.id);
    }

    this.configService.updateConfig(config);
  }

  private extractConfig(res: any): VexConfig | null {
    const row = res?.data || res;
    const config = row?.configjson || row?.config_json || row?.config;
    return config ? this.cloneConfig(config) : null;
  }

  private readLocalConfig(userId: number): VexConfig | null {
    try {
      const raw = localStorage.getItem(this.localKey(userId));
      return raw ? this.cloneConfig(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }

  private saveLocalConfig(userId: number, config: VexConfig): void {
    localStorage.setItem(this.localKey(userId), this.toJson(config));
  }

  private cloneConfig(config: VexConfig): VexConfig {
    return JSON.parse(JSON.stringify(config)) as VexConfig;
  }

  private toJson(config: VexConfig): string {
    return JSON.stringify(config);
  }

  private localKey(userId: number): string {
    return `user_theme_preference_${userId}_${this.platform}`;
  }

  private sessionLoadedKey(userId: number): string {
    return `user_theme_preference_loaded_${userId}_${this.platform}`;
  }

  private getUserId(): number {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) return 0;

    try {
      const decode: any = JSON.parse(atob(token.split('.')[1]));
      return Number(decode?.unique_name || 0);
    } catch {
      return 0;
    }
  }
}
